import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { card, input, label, btnPrimary, btnGhost, currency, fmtDate } from "../ui";
import { Loader2, Wallet, Clock, CheckCircle2, Undo2, CalendarDays, Pencil, CalendarClock } from "lucide-react";

const statusLabel = { agendada: "Agendada", realizada: "Realizada", falta: "Falta", cancelada: "Cancelada" };

export default function PatientFinance({ patientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editTarget, setEditTarget] = useState(null);

  const load = () =>
    api.get(`/patients/${patientId}/finance`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, [patientId]);

  const togglePay = async (s) => {
    const next = s.payment_status === "pago" ? "pendente" : "pago";
    setBusy(s.id);
    try {
      await api.patch(`/sessions/${s.id}/payment`, { status: next });
      toast.success(next === "pago" ? "Marcado como pago." : "Marcado como pendente.");
      await load();
    } catch { toast.error("Não consegui atualizar o pagamento."); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" /></div>;
  if (!data) return <p className="font-body text-sm text-[#7A8A7A]">Não foi possível carregar o financeiro.</p>;

  const stat = [
    { label: "Recebido", value: currency(data.total_paid), sub: `${data.paid_count} sessão(ões)`, icon: CheckCircle2, tone: "text-[#5B6E5B]", bg: "bg-[#5B6E5B]/10" },
    { label: "A receber", value: currency(data.total_pending), sub: `${data.pending_count} sessão(ões) já passadas`, icon: Clock, tone: "text-[#C87A5E]", bg: "bg-[#C87A5E]/12" },
    { label: "Previsto", value: currency(data.total_forecast), sub: `${data.forecast_count} sessão(ões) futuras`, icon: CalendarClock, tone: "text-[#7A8A7A]", bg: "bg-[#F5F0E6]" },
    { label: "Total de sessões", value: data.total_sessions, sub: "no histórico", icon: CalendarDays, tone: "text-[#4A5D4E]", bg: "bg-[#F5F0E6]" },
  ];

  return (
    <div data-testid="patient-finance" className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stat.map((s) => (
          <div key={s.label} className={`${card} p-5`} data-testid={`finance-stat-${s.label}`}>
            <div className={`inline-grid place-items-center w-10 h-10 rounded-xl ${s.bg} ${s.tone}`}><s.icon className="w-5 h-5" /></div>
            <p className="font-body text-xs tracking-[0.2em] uppercase text-[#7A8A7A] mt-3">{s.label}</p>
            <p className="font-display text-2xl text-[#2C332A] mt-1">{s.value}</p>
            <p className="font-body text-xs text-[#A8B0A2] mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-[#5B6E5B]/10 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#5B6E5B]" />
          <p className="font-display text-lg text-[#2C332A]">Sessões e pagamentos</p>
        </div>
        {data.sessions.length === 0 ? (
          <p className="px-5 py-8 font-body text-sm text-[#7A8A7A] text-center">Nenhuma sessão registrada para este paciente ainda.</p>
        ) : (
          <div className="divide-y divide-[#5B6E5B]/8">
            {data.sessions.map((s) => {
              const paid = s.payment_status === "pago";
              const cancelled = s.status === "cancelada";
              return (
                <div key={s.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="finance-session-row">
                  <div className="min-w-0">
                    <p className="font-body text-sm text-[#2C332A]">{fmtDate(s.date)} · {s.time}</p>
                    <p className="font-body text-xs text-[#A8B0A2]">{statusLabel[s.status] || s.status}{paid && s.payment_date ? ` · pago em ${fmtDate(s.payment_date)}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-body text-sm font-medium text-[#2C332A]">{currency(s.price)}</span>
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-body ${paid ? "bg-[#5B6E5B]/12 text-[#4A5D4E]" : "bg-[#C87A5E]/15 text-[#B2644D]"}`} data-testid="finance-pay-badge">
                      {paid ? "Pago" : "Pendente"}
                    </span>
                    <button onClick={() => setEditTarget(s)} data-testid={`finance-edit-${s.id}`} title="Editar sessão"
                      className="grid place-items-center w-8 h-8 rounded-full border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!cancelled && (
                      <button onClick={() => togglePay(s)} disabled={busy === s.id} data-testid="finance-toggle-pay"
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors disabled:opacity-60 ${paid ? "border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-[#F5F0E6]" : "bg-[#5B6E5B] text-[#FDFBF7] hover:bg-[#4A5D4E]"}`}>
                        {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (paid ? <Undo2 className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />)}
                        {paid ? "Marcar pendente" : "Marcar pago"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SessionEditModal
        session={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); load(); toast.success("Sessão atualizada."); }}
      />
    </div>
  );
}

function SessionEditModal({ session, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (session) {
      setForm({
        date: session.date || "", time: session.time || "", price: session.price ?? 0,
        status: session.status || "agendada", payment_status: session.payment_status || "pendente",
        notes: session.notes || "",
      });
    }
  }, [session]);

  if (!session || !form) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        patient_id: session.patient_id || null,
        patient_name: session.patient_name,
        date: form.date, time: form.time,
        duration_min: session.duration_min || 50,
        status: form.status,
        price: parseFloat(form.price) || 0,
        payment_status: form.payment_status,
        payment_date: form.payment_status === "pago"
          ? (session.payment_date || new Date().toISOString().slice(0, 10))
          : null,
        notes: form.notes,
        slot_id: session.slot_id || null,
        meet_link: session.meet_link || "",
      };
      await api.put(`/sessions/${session.id}`, payload);
      setSaving(false);
      onSaved();
    } catch {
      toast.error("Não consegui salvar a sessão.");
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!session} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#FDFBF7]" data-testid="session-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C332A]">Editar sessão</DialogTitle>
          <DialogDescription className="sr-only">Editar valor, data, horário, status e pagamento da sessão</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="session-edit-form">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>Data</label><input type="date" className={input} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="edit-date" required /></div>
            <div><label className={label}>Horário</label><input type="time" className={input} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="edit-time" required /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>Valor (R$)</label><input type="number" min="0" step="0.01" className={input} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-price" required /></div>
            <div>
              <label className={label}>Status da sessão</label>
              <select className={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-status">
                <option value="agendada">Agendada</option>
                <option value="realizada">Realizada</option>
                <option value="falta">Falta</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
          </div>
          <div>
            <label className={label}>Pagamento</label>
            <select className={input} value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="edit-payment">
              <option value="pendente">Pendente</option>
              <option value="pago">Pago</option>
            </select>
          </div>
          <div><label className={label}>Observações</label><textarea rows={2} className={`${input} resize-none`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" /></div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" className={btnGhost} onClick={onClose} data-testid="edit-cancel">Cancelar</button>
            <button type="submit" className={btnPrimary} disabled={saving} data-testid="edit-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar alterações"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
